import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { Inventory } from 'public-contracts';
import { inventoryQRService } from '../services/inventoryQRService';
import {
  InventoryQRScannerDialog,
  InventoryQRData,
} from '../components/InventoryQRScannerDialog';
import { InventoryStockUpdateDialog } from './InventoryStockUpdateDialog';
import { InventoryService } from '../../services/inventoryService';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { QrCode, Package, Download, Scan, AlertCircle } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';

interface InventoryQRManagerProps {
  inventory: Inventory;
  onClose: () => void;
}

export const InventoryQRManager: React.FC<InventoryQRManagerProps> = ({
  inventory,
  onClose,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [qrCodeSVG, setQrCodeSVG] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [stockUpdateDialogOpen, setStockUpdateDialogOpen] = useState(false);
  const [scannedInventory, setScannedInventory] = useState<Inventory | null>(null);

  const generateQRCode = async () => {
    setIsGenerating(true);
    try {
      const svg = await inventoryQRService.generateQRCodeSVG(
        inventory.id,
        inventory.code,
        inventory.name
      );
      setQrCodeSVG(svg);
      notify('QR code generated successfully', { type: 'success' });
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      notify('Failed to generate QR code', { type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeSVG) return;

    const blob = new Blob([qrCodeSVG], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-${inventory.code}-qr.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notify('QR code downloaded', { type: 'success' });
  };

  const handleQRScanSuccess = async (inventoryData: InventoryQRData) => {
    console.log('Scanned inventory QR:', inventoryData);
    setIsScannerOpen(false);

    try {
      // Fetch the full inventory data
      const scannedInv = await InventoryService.getInventory(inventoryData.inventoryId);
      setScannedInventory(scannedInv);
      setStockUpdateDialogOpen(true);
      notify(`Scanned inventory: ${inventoryData.inventoryName}`, {
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to load inventory:', error);
      notify('Failed to load inventory data', { type: 'error' });
    }
  };

  const handleStockUpdateSuccess = () => {
    // Refresh inventory data if needed
    setStockUpdateDialogOpen(false);
    setScannedInventory(null);
  };

  const handleQRScanError = (error?: string) => {
    console.error('QR scan error:', error);
    notify(error || 'QR scan failed', { type: 'error' });
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              {translate('pos.inventory.qrCodeManager', 'QR Code Manager')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Inventory Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <span className="font-medium">{inventory.name}</span>
                <Badge variant="outline">{inventory.code}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {translate('pos.inventory.currentQuantity', 'Current Quantity')}
                : {inventory.quantity}
              </p>
            </div>

            <Separator />

            {/* QR Code Generation */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  {translate(
                    'pos.inventory.generateQRCode',
                    'Generate QR Code'
                  )}
                </h3>
                <Button
                  onClick={generateQRCode}
                  disabled={isGenerating}
                  size="sm"
                  variant="outline"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                      {translate('pos.inventory.generating', 'Generating...')}
                    </>
                  ) : (
                    <>
                      <QrCode className="h-3 w-3 mr-2" />
                      {translate('pos.inventory.generate', 'Generate')}
                    </>
                  )}
                </Button>
              </div>

              {qrCodeSVG && (
                <div className="space-y-2">
                  <div className="flex justify-center p-4 bg-muted rounded-lg">
                    <div
                      dangerouslySetInnerHTML={{ __html: qrCodeSVG }}
                      className="max-w-[200px]"
                    />
                  </div>
                  <Button
                    onClick={downloadQRCode}
                    size="sm"
                    className="w-full"
                    variant="outline"
                  >
                    <Download className="h-3 w-3 mr-2" />
                    {translate('pos.inventory.downloadQR', 'Download QR Code')}
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            {/* QR Code Scanning */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  {translate('pos.inventory.scanQRCode', 'Scan QR Code')}
                </h3>
                <Button
                  onClick={() => setIsScannerOpen(true)}
                  size="sm"
                  variant="outline"
                >
                  <Scan className="h-3 w-3 mr-2" />
                  {translate('pos.inventory.scan', 'Scan')}
                </Button>
              </div>

              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {translate(
                      'pos.inventory.scanInstructions',
                      'Scan Instructions'
                    )}
                  </p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>
                      •{' '}
                      {translate(
                        'pos.inventory.scanForCounting',
                        'Scan to count inventory'
                      )}
                    </li>
                    <li>
                      •{' '}
                      {translate(
                        'pos.inventory.scanForDeduction',
                        'Scan to deduct stock'
                      )}
                    </li>
                    <li>
                      •{' '}
                      {translate(
                        'pos.inventory.scanForAdjustment',
                        'Scan to adjust quantities'
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                {translate('pos.common.action.close')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <InventoryQRScannerDialog
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onSuccess={handleQRScanSuccess}
        onError={handleQRScanError}
      />

      {/* Stock Update Dialog */}
      <InventoryStockUpdateDialog
        open={stockUpdateDialogOpen}
        onClose={() => {
          setStockUpdateDialogOpen(false);
          setScannedInventory(null);
        }}
        inventory={scannedInventory}
        onSuccess={handleStockUpdateSuccess}
      />
    </>
  );
};
